import Modal from "@via/components/modal";
import { HorizontalTabs } from "@via/components/tabs";
import CloudPanel from "@via/pages/account/admin/organizations/panels/cloud-panel.tsx";
import EntitlementsPanel from "@via/pages/account/admin/organizations/panels/entitlements-panel.tsx";
import GeneralPanel, {
  type PanelSubmitHandle,
} from "@via/pages/account/admin/organizations/panels/general-panel.tsx";
import MembersPanel from "@via/pages/account/admin/organizations/panels/members-panel.tsx";
import RunEnvironmentsPanel from "@via/pages/account/admin/organizations/panels/run-environments-panel.tsx";
import StoragePanel from "@via/pages/account/admin/organizations/panels/storage-panel.tsx";
import { ModalSize } from "@via/utils/constants";
import { type Tab } from "@via/utils/interfaces.tsx";
import { GetUserOrganizationDetailsForAdminResponseBodyItem } from "common";
import { useRef, useState, type JSX } from "react";

export type OrganizationTab =
  | "General"
  | "Members"
  | "Entitlements"
  | "Cloud"
  | "Run Environments"
  | "Storage";

const ORGANIZATION_MODAL_TABS: Array<Tab<OrganizationTab>> = [
  { name: "General" },
  { name: "Members" },
  { name: "Entitlements" },
  { name: "Cloud" },
  { name: "Run Environments" },
  { name: "Storage" },
];

/**
 * One door for everything configured on an organization, replacing six context menu entries that
 * each opened their own dialog with their own idea of what an organization is.
 *
 * Tabs are deliberately of two kinds. General is a form: several fields, one Save, abandonable.
 * Members, Cloud and Storage are live lists where each add and remove commits on its own, which is
 * how their endpoints are shaped. Confirm is therefore shown only on the form tabs, because
 * offering it on a list tab would imply a save that has already happened.
 */
export default function OrganizationModal({
  organization,
  isOpen,
  setIsOpen,
}: {
  organization: GetUserOrganizationDetailsForAdminResponseBodyItem | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}): JSX.Element | null {
  const [activeTab, setActiveTab] = useState<OrganizationTab>("General");
  const generalRef = useRef<PanelSubmitHandle>(null);
  const entitlementsRef = useRef<PanelSubmitHandle>(null);

  if (!organization) return null;

  const isFormTab = activeTab === "General" || activeTab === "Entitlements";

  const handleConfirm = async (): Promise<boolean> => {
    if (activeTab === "General") {
      return (await generalRef.current?.submit()) ?? false;
    }
    if (activeTab === "Entitlements") {
      return (await entitlementsRef.current?.submit()) ?? false;
    }
    return true;
  };

  return (
    <Modal
      open={isOpen}
      setOpen={setIsOpen}
      title={`Manage ${organization.name}`}
      size={ModalSize.Large}
      showButtons={isFormTab}
      confirmText="Save"
      onConfirm={handleConfirm}>
      <HorizontalTabs
        options={ORGANIZATION_MODAL_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        variant="button-group"
        buttonIdPrefix="organization-modal"
      />
      <div className="pt-4">
        {activeTab === "General" && (
          <GeneralPanel ref={generalRef} organization={organization} />
        )}
        {activeTab === "Members" && (
          <MembersPanel organization={organization} />
        )}
        {activeTab === "Entitlements" && (
          <EntitlementsPanel
            ref={entitlementsRef}
            organization={organization}
          />
        )}
        {activeTab === "Cloud" && (
          <CloudPanel
            organization={organization}
            onNavigateToTab={setActiveTab}
          />
        )}
        {activeTab === "Run Environments" && (
          <RunEnvironmentsPanel organization={organization} />
        )}
        {activeTab === "Storage" && (
          <StoragePanel organization={organization} />
        )}
      </div>
    </Modal>
  );
}
